import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map, mergeMap, take } from 'rxjs/operators';
import { CmsService } from './cms/cms.service';
import { AlertService } from './services/alert.service';
import { AuthService } from './services/auth.service';
import { ChatService } from './services/chat.service';
import { CookieService } from './services/cookie.service';
import { DomainService } from './services/domain.service';
import { MenuService } from './services/menu.service';
import { NotificationService } from './services/notification.service';
import { OrdersService } from './services/orders.service';
import { OverlayService } from './services/overlay.service';
import { StorageService } from './services/storage.service';
import { TwilioService } from './services/twilio.service';
import { Store } from './store';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
    user$ = this.store.select<any>('user');
    alerts$ = this.store.select<any>('alerts');
    overlays$ = this.store.select<any>('overlays');
    asideOpen$ = this.store.select<boolean>('asideOpen');
    hideMenu$ = this.store.select<boolean>('hideMenu');
    public innerWidth: any;

    currentURL = '';
    colouredBackground = false;
    noPaddedTop = false;
    offset = false;
    showFooter = false;
    showMenu = false;
    showHeader = false;
    fullScreen = false;
    compactBoardLayout = false;

    intervalId: any;

    @HostListener('window:beforeunload', ['$event']) beforeunloadHandler(event: Event) {
        this.chatService.sendOfflineSignal();
        this.twilioService.destroyDevices();
    }

    @HostListener('window:unload', ['$event']) unloadHandler(event: Event) {

    }

    @HostListener('document:keydown.escape', ['$event']) onKeydownHandler(event: KeyboardEvent) {
        this.overlayService.closeAll();
    }

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private store: Store,
        private authService: AuthService,
        private cookieService: CookieService,
        private storageService: StorageService,
        private alertService: AlertService,
        private overlayService: OverlayService,
        private chatService: ChatService,
        private notificationService: NotificationService,
        private cmsService: CmsService,
        private domainService: DomainService,
        private ordersService: OrdersService,
        public menuService: MenuService,
        public twilioService: TwilioService
    ) {
        this.domainService.findAll();
        this.storageService.check();
        this.storageService.checkUser();
        this.cookieService.check();
        this.sendOnlineSignal();
        this.cmsService.getSettings().subscribe();
    }

    ngOnInit(): void {
        this.setDomain();
        this.innerWidth = window.innerWidth;

        // getOrderLineStatusGroups is a session-guarded endpoint. Firing it on every
        // bootstrap (including the unauthenticated login page) returns 403 AUTH-0002,
        // which the error interceptor used to turn into a logout/resetLogin bounce. Wait
        // for an authenticated user (BehaviorSubject replays the current value, so this
        // fires immediately on a reload that already has a session, or once at sign-in).
        this.store
            .select<any>('user')
            .pipe(
                filter((user) => !!user),
                take(1)
            )
            .subscribe(() => {
                this.ordersService.getOrderLineStatusGroups().subscribe((data) => {
                    this.store.set('statusGroups', data);
                });
            });

        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                map(() => this.route),
                map((route) => {
                    while (route.firstChild) {
                        route = route.firstChild;
                        this.checkSignInStatus(route.snapshot.routeConfig.path);
                        return route;
                    }
                }),
                filter((route) => route.outlet === 'primary'),
                mergeMap((route) => {
                    return route.data;
                })
            )
            .subscribe((event) => {
                if (event.fullScreen) {
                    this.fullScreen = true;
                } else {
                    this.fullScreen = false;
                }
                if (event.showMenu) {
                    this.showMenu = event.showMenu;
                }
                if (event.showHeader) {
                    this.showHeader = event.showHeader;
                }
                if (event.showFooter) {
                    this.showFooter = event.showFooter;
                }
                if (event.colouredBackground) {
                    this.colouredBackground = event.colouredBackground;
                } else {
                    this.colouredBackground = false;
                }
                if (event.noPaddedTop) {
                    this.noPaddedTop = event.noPaddedTop;
                } else {
                    this.noPaddedTop = false;
                }
                if (event.offset) {
                    this.offset = event.offset;
                } else {
                    this.offset = false;
                }
                this.compactBoardLayout = event.compactBoardLayout || this.isCompactBoardRoute(this.router.url);
                this.showFooter = event.showFooter;
            });
        //PLEASE DO NOT REMOVE
        console.log(this.store);
    }

    ngOnDestroy(): void {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
    }

    sendOnlineSignal() {
        this.intervalId = setInterval(() => {
            this.chatService.sendOnlineSignal();
        }, 5000);
    }

    checkSignInStatus(url) {
        if (!this.store.selectForLocal('user')) {
            if (url) {
                // this.router.navigate(['']);
            }
        } else {
            if (!url) {
                this.router.navigate(['dashboard']);
            }
        }
    }

    onActivate(event) {
        window.scroll(0, 0);
    }

    closeAll() {
        this.overlayService.closeAll();
    }

    private isCompactBoardRoute(url: string): boolean {
        return /^\/orders(?:\/(?:samples|board))?(?:[?#].*)?$/.test(url || '');
    }

    setDomain() {
        const domainId = localStorage.getItem('activeDomain');
        this.store.set('domain', domainId ? domainId : 'ys');
    }
}
